<template>
  <aside class="side-navigation" :class="{'collapsed': collapsed}">
    <AdminNavigation v-if="isAllotmentApp && (isAdmin || isReadonlyAdmin)" />
    <FinanceNavigation v-if="isAllotmentApp && isFinance" />
    <TourOperatorNavigation v-if="isAllotmentApp && isTourOperator" />
    <HccReadonlyNavigation v-if="isAllotmentApp && isHccReadonly" />
    <CharterNavigation v-if="isCharterApp" />
    <span class="handle text-color cursor-pointer" @click="toggle"></span>

  </aside>
</template>
<script setup lang="ts">
import {ref} from 'vue';
import { useCurrentAppStore } from '@/stores/current-app-store';
import AdminNavigation from './Admin.vue';
import FinanceNavigation from './Finance.vue';
import TourOperatorNavigation from './TourOperator.vue';
import HccReadonlyNavigation from './HccReadonly.vue';
import CharterNavigation from './Charter.vue';
import {storeToRefs} from "pinia";
import {useCurrentUserStore} from "@/stores/current-user-store.ts";

const currentUserStore = useCurrentUserStore()
const { isAdmin, isReadonlyAdmin, isFinance, isTourOperator, isHccReadonly } = storeToRefs(currentUserStore)

const currentAppStore = useCurrentAppStore()
const { isAllotmentApp, isCharterApp } = storeToRefs(currentAppStore)

const collapsed = ref<boolean>(getInitialCollapsedState());

function getInitialCollapsedState(): boolean {
  const value = localStorage.getItem('sideMenuStateCollapsed');

  if (value === null) {
    return false;
  }


  try {
    return JSON.parse(value) === true;
  } catch {
    return false;
  }
}

async function toggle() {
  collapsed.value = !collapsed.value;
  localStorage.setItem('sideMenuStateCollapsed', JSON.stringify(collapsed.value));
}
</script>
<style lang="scss">
@use '@/assets/sass/vendors/prime/theme/variables/misc' as badge;

.side-navigation {
  display: flex;
  flex-basis: 12.5rem;
  background: var(--c_white);
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
  font-size: 0.875rem;
  overflow-y: scroll;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &:hover,
  &:focus-within {
    .handle {
      opacity: 1;
    }
  }

  h2 {
    font-size: inherit;
    font-weight: bold;
    margin-bottom: 0.625rem;
    margin-left: 0.625rem;
    text-transform: uppercase;
    color: var(--c_primary);

    &:first-child {
      margin-top: 1.75rem;
    }
  }

  &.collapsed {
    flex-basis: 2.75rem !important;
    padding-top: 0.9375rem;

    h2 {
      display: none;
    }

    menu {
      li {
        a {
          width: 1.5rem;
          overflow: visible;

          .text {
            width: 0;
            overflow: hidden;
          }

          .notifications {
            position: absolute;
            transform: translate(14px, -8px);
          }
        }
      }
    }

    .handle {
      opacity: 1;
      border-left: 10px solid var(--c_grey--medium);
      border-right-color: transparent;
      transform: translateX(0.625rem);
      left: 2.125rem;
    }
  }

  nav {
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
    justify-content: flex-start;
  }

  menu {
    padding: 0;
    margin-top: 0;
    list-style-type: none;

    li {
      padding: 0.125rem 0.625rem;

      a {
        color: var(--c_primary);
        display: flex;
        align-items: flex-end;
        text-decoration: none;
        white-space: nowrap;
        position: relative;


        span {
          display: inline-flex;
          align-items: flex-start;
          margin-right: 0.1875rem;
        }

        .notifications {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          min-width: badge.$badgeMinWidth;
          height: badge.$badgeHeight;
          padding: 0 0.25rem;
          font-size: badge.$badgeFontSize;
          line-height: 1;
          transform: translate(-2px, -5px);

          &.p-badge-circle {
            padding: 0;
          }
        }

        &:hover {
          color: var(--c_primary--hover);
          text-decoration: none;
        }
      }

      &.active {
        background-color: var(--c_primary);

        a {
          color: var(--c_white);
        }
      }
    }
  }

  .handle {
    display: block;
    opacity: 0;
    transition: opacity .0825s ease-out;
    align-self: center;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid var(--c_grey--medium);
    position: fixed;
    transform: translateX(-0.625rem);
    top: calc(50vh);
    left: 12.5rem;
  }
}
</style>
